<template>
    <date-range-picker
        :id="`date-range-picker-${id}`"
        ref="picker"
        v-model="dateRange"
        opens="center"
        :locale-data="{ firstDay: 1, format: 'dd-mm-yyyy HH:mm:ss' }"
        :minDate="minDate"
        :autoApply="true"
        :ranges="false"
        :maxDate="maxDate"
        :singleDatePicker="singleDatePicker"
        :timePicker="timePicker"
        :timePicker24Hour="timePicker24Hour"
        @update="updateValues"
        @toggle="checkOpen"
    >
        <template #input>
            <div class="input-wrapper flex flex--align-flex-end flex--justify-space-between">
                <span>{{ $t('global.dates.date') }}</span>

                <div class="custom-select"
                    tabindex="0"
                    @blur="open = false"
                >
                    <div class="selected"
                        @click="toggleSelect"
                    >
                        <span>{{ $t(`global.dates.${selected.value}`) }}</span>
                        <i :id="`date-range-picker-chevron-${id}`"
                            class="icon-chevron"
                        />
                    </div>

                    <div :class="['items', { 'items--hide-select-dropdown': !open }]">
                        <div v-for="(option, i) of options"
                            :id="`date-range-picker-item-${id}-${i}`"
                            :key="i"
                            :class="['items__item', { 'items__item--selected': option.text === selected.text }]"
                            @click="onClickSelectItem(option)"
                        >
                            {{ $t(`global.dates.${option.value}`) }}
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </date-range-picker>
</template>

<script>
// 3rd Party
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

// Utils
import {
    subWeeks,
    subMonths,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    subDays,
    startOfDay,
    endOfDay,
} from '@shared/utils/dates';

// Const
import {
    dateRangePickerConst,
} from './date-range-picker.const';

export default {
    components: {
        DateRangePicker,
    },
    props: {
        // ID for automation
        id: {
            type: String,
            default: '',
        },
        singleDatePicker: {
            type: String,
            default: 'range',
        },
        minDate: {
            type: String,
            default: Date.now().toString(),
        },
        maxDate: {
            type: String,
            default: null,
        },
        timePicker: {
            type: Boolean,
            default: false,
        },
        timePicker24Hour: {
            type: Boolean,
            default: true,
        },
        // Flag whether to automatically close the calender once the date is selected
        closeOnSelection: {
            type: Boolean,
            default: false,
        },
        // Should be an object with startDate and endDate, endDate can be set to null
        defaultRange: {
            type: Object,
            default: () => ({}),
        },
        isOpen: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        const defaultSelection = dateRangePickerConst.preSelectedRanges[0];

        return {
            dateRange: this.defaultRange,
            open: false,
            selected: defaultSelection,
            options: dateRangePickerConst.preSelectedRanges,
        };
    },
    watch: {
        isOpen(value) {
            this.$refs.picker.togglePicker(value);
        },

        defaultRange(value) {
            this.dateRange = value;
        },
    },
    mounted() {
        // Cannot be handled solely in the watcher due to the immediate being run on created and the refs are still not defined
        this.$refs.picker.togglePicker(this.isOpen);

        // Emit only if date range has a value
        if (this.dateRange.startDate) {
            this.$emit('dateChanged', {
                id: Number.MAX_SAFE_INTEGER,
                ...this.dateRange,
            });
        }
    },
    methods: {
        updateValues({
            startDate = '',
            endDate = '',
        }) {
            this.$refs.picker.togglePicker(!this.closeOnSelection);

            const processedDateObj = {
                id: Number.MAX_SAFE_INTEGER,
                startDate: startOfDay(startDate),
                endDate: endOfDay(endDate),
            };

            this.$emit('dateChanged', processedDateObj);
        },
        checkOpen() {
            this.$refs.picker.togglePicker(!this.closeOnSelection);
        },
        toggleSelect() {
            this.open = !this.open;
        },
        onClickSelectItem(option) {
            this.open = false;
            this.selected = option;
            this.dateRange = this.getPreSelectedRange(option.value);
            this.$emit('dateChanged', {
                id: Number.MAX_SAFE_INTEGER,
                ...this.dateRange,
            });
        },
        // Consider moving to a helper later on if needed
        getPreSelectedRange(range) {
            const todayStart = startOfDay(new Date());
            const todayEnd = endOfDay(new Date());

            const ranges = {
                today: {
                    startDate: todayStart,
                    endDate: todayEnd,
                },
                lastWeek: {
                    startDate: startOfWeek(subWeeks(todayStart, 1), {
                        weekStartsOn: 1,
                    }),
                    endDate: endOfWeek(subWeeks(todayEnd, 1), {
                        weekStartsOn: 1,
                    }),
                },
                thisWeek: {
                    startDate: startOfWeek(todayStart, {
                        weekStartsOn: 1,
                    }),
                    endDate: endOfWeek(todayEnd, {
                        weekStartsOn: 1,
                    }),
                },
                last7Days: {
                    startDate: subDays(todayStart, 7),
                    endDate: todayEnd,
                },
                thisMonth: {
                    startDate: startOfMonth(todayStart),
                    endDate: endOfMonth(todayEnd),
                },
                lastMonth: {
                    startDate: startOfMonth(subMonths(todayStart, 1)),
                    endDate: endOfMonth(subMonths(todayEnd, 1)),
                },
                last30Days: {
                    startDate: subDays(todayStart, 30),
                    endDate: todayEnd,
                },
            };

            return ranges[range] ?? {
                startDate: null,
                endDate: null,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.input-wrapper {
    border-bottom: 1px solid $dusty-gray-color;
    padding: 0 0.5em 0.5em 0.5em;
    box-sizing: content-box;
    cursor: default;
    min-width: 22.5rem;

    span {
        font-size: 1.1rem;
        font-weight: 600;
        flex: 1;
    }
}

.custom-select {
    position: relative;
    width: 100%;
    text-align: left;
    outline: none;
    z-index: 3002;
    flex: 0.8;
}

.selected {
    background-color: transparent;
    color: $cyprus-color;
    font-size: 1.1rem;
    padding-left: 0.8em;
    letter-spacing: 0.055px;
    cursor: pointer;
    position: relative;

    span {
        font-weight: 500;
    }
}

.icon-chevron {
    position: absolute;
    color: $cyprus-color;
    font-size: 0.7rem;
    top: 50%;
    right: 8px;
    transform: translateY(-50%) rotate(180deg);
}

.items {
    color: $cyprus-color;
    overflow: hidden;
    position: absolute;
    background-color: $white-color;
    line-height: 4rem;
    left: 0;
    right: 0;
    z-index: 1;
    box-shadow: rgba(0, 0, 0, 0.2) 0 0 10px 0;

    &--hide-select-dropdown {
        display: none;
    }

    &__item {
        color: $cyprus-color;
        padding-left: 1em;
        cursor: pointer;
        font-size: 1.1rem;

        &:hover {
            background-color: $lima-color;
            color: $white-color;
        }

        &--selected {
            font-weight: 600;
        }
    }
}
</style>